import React, { useRef } from 'react';
import { BackBar, BackButton } from './back-button';
import { useOutlet, useLocation, NavigateFunction } from 'react-router-dom';
import styled from '@emotion/styled';
import { StepTransition } from '../../motion/step-transition';
import {
  NavbarAndContentContainer,
  NavbarContainer,
  ContentAndButtonsContainer,
  StepAndProgressContainer,
} from './layout-container.styled';
import { Navbar } from './visitor-log/navbar';
import { Progress } from '../progress/progress';
import { findPathIndex } from '../pages/visitor-log/shared/controllers/find-path-index';
import { spacing } from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';

/* The card that holds the active step. Rendered ONCE and never remounted — the
   content inside it is what swaps. */
const StepPane = styled.div`
  flex: 1;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* No card treatment here: this pane already sits INSIDE the
     NavbarAndContentContainer card, so a second surface/border/shadow produced a
     visible card-in-a-card. Keep only the spacing; the outer container is the
     single card. */
  background: transparent;
  padding: ${spacing.lg};

  ${down('md')} {
    padding: ${spacing.md};
  }
`;

/* The step layer fills the pane like the step used to; the directional slide
   itself is the shared StepTransition (motion/step-transition.tsx), which also
   animates the OUTGOING step — the CSS keyframe it replaces only ever animated
   arrivals. */
const STEP_LAYER_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
};

interface WizardLayoutProps {
  navigate: NavigateFunction;
  navigation: string[];
  state: object;
  selectedOption: string;
  // eslint-disable-next-line
  selectedBuilding?: any;
}

/**
 * Persistent shell for the visitor-log / daily-log wizard.
 *
 * Every step used to render this entire tree itself, so navigating unmounted and
 * rebuilt the sidebar, building panel, rail, step and progress bar — the flash.
 * Now the shell mounts once and only the step pane changes, which also lets
 * the progress bar tween and the rail's active pill slide.
 *
 * The rail, the building panel and the `<main>` are NOT here any more: they
 * belong to DeskShell (layout/desk-shell.tsx), which this layout nests inside,
 * so they survive the trip from the main menu into a wizard and back.
 *
 * `useOutlet()` rather than `<Outlet />`: the leaving step keeps rendering its
 * own route for the ~90ms of its exit instead of flashing the arriving one.
 */
export const WizardLayout: React.FC<WizardLayoutProps> = ({
  navigate,
  navigation,
  state,
}) => {
  const location = useLocation();
  const outlet = useOutlet();
  const path = location.pathname;
  const isLog = path.includes('/daily-log');
  const maxPaths = navigation?.length || 0;
  const currentPathIndex = findPathIndex(path, navigation);

  // The type/activity pickers are step 1 of each wizard but never appear in
  // the navigation array (index -1). Before a type is picked the array is
  // empty too — clamp so the header reads "Step 1", never "Step -1".
  const displayIndex =
    currentPathIndex < 0 && maxPaths === 0 ? 1 : currentPathIndex;

  // Remember the previous step to decide the slide direction. The refs only
  // move on a REAL path change — re-renders at the same location (e.g. the
  // picker's finish() state reset) must not recompute `back` mid-animation.
  const prevPathRef = useRef(path);
  const prevIndexRef = useRef(currentPathIndex);
  const backRef = useRef(false);

  if (path !== prevPathRef.current) {
    backRef.current = currentPathIndex < prevIndexRef.current;
    prevPathRef.current = path;
    prevIndexRef.current = currentPathIndex;
  }

  const back = backRef.current;

  return (
    <>
        {/* ONE POSITION INDICATOR, NOT TWO. A <Step /> node reading "Step 3"
            used to sit immediately left of this, stating the reader's position
            a second time in the same row and without a denominator. Progress
            says the same number, says what it is out of, and draws the distance
            left to run. */}
        <StepAndProgressContainer>
          <Progress currentPathIndex={displayIndex} maxPaths={maxPaths} />
        </StepAndProgressContainer>
        <NavbarAndContentContainer>
          <NavbarContainer>
            <Navbar
              path={path}
              navigation={navigation}
              state={state}
              isLog={isLog}
              navigate={navigate}
              currentPage={path}
              hasContent
            />
          </NavbarContainer>
          <ContentAndButtonsContainer>
            <StepPane>
              {/* THE WAY OUT, ONCE, FOR ALL 51 WIZARD ROUTES (WP-10 / BUG-011).
                  Three routes rendered their own copy of this — visitor-log,
                  daily-log and resident-entry — and every route past the first
                  step of those flows had none at all. So the owner opened a
                  visitor-log entry and had no way back to the menu, which is
                  the report.

                  It also caused a measurable jump: this bar is 44px min-height
                  plus 16px margin, so stepping from the picker into a step made
                  the title rise ~60px. That is BUG-019's visitor-log half, and
                  it closes here rather than in a layout fix.

                  INSIDE StepPane, above Slide, deliberately. Outside StepPane it
                  loses the pane's padding and stops sharing a left edge with the
                  step title, which back-button.tsx:138-155 says is the entire
                  point of the treatment. Above Slide, not inside it, so the
                  remount on every step change does not take the exit with it. */}
              <BackBar>
                <BackButton to="/main-menu" label="Back to menu" />
              </BackBar>
              <StepTransition
                stepKey={path}
                direction={back ? 'back' : 'forward'}
                className="wizard-slide"
                style={STEP_LAYER_STYLE}
              >
                {outlet}
              </StepTransition>
            </StepPane>
          </ContentAndButtonsContainer>
        </NavbarAndContentContainer>
    </>
  );
};
